import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

export type Language = 'en' | 'zh';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'seaing-you-language';

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [language, setLanguageState] = useState<Language>(() => {
    // Priority 1: Check URL parameter
    const urlLang = searchParams.get('lang');
    if (urlLang === 'en' || urlLang === 'zh') {
      return urlLang;
    }

    // Priority 2: Check localStorage
    try {
      const storedLang = localStorage.getItem(STORAGE_KEY);
      if (storedLang === 'en' || storedLang === 'zh') {
        return storedLang;
      }
    } catch (error) {
      console.warn('localStorage not available:', error);
    }

    // Priority 3: Default to English
    return 'en';
  });

  // Update URL parameter when language changes
  useEffect(() => {
    const currentLang = searchParams.get('lang');
    if (currentLang !== language) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('lang', language);
        return newParams;
      }, { replace: true });
    }
  }, [language, searchParams, setSearchParams]);

  // Persist language to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch (error) {
      console.warn('Failed to save language preference:', error);
    }
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
